import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import type { RetrievalEstimate, RetrievalResult } from '../api/types';
import { useAsync } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatEUR, formatNumber } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState, ErrorState, PendingState } from '../components/States';
import { Button, Card, CardHead, IdChip, Spinner, Stat } from '../components/ui';
import { Field } from '../components/Field';
import { useToast } from '../components/Toast';
import { IconRetrieval, IconWarning, IconCheck, IconBilling, IconDownload, IconCloud } from '../components/Icons';

export function Retrieval() {
  const { t } = useTranslation();
  const archives = useAsync(() => api.listArchives(200), []);

  return (
    <>
      <PageHeader title={t('retrieval.title')} subtitle={t('retrieval.subtitle')} />
      <ApiState {...archives} onRetry={archives.reload}>
        {(page) =>
          page.items.length === 0 ? (
            <Card>
              <EmptyState title={t('archives.empty')} />
            </Card>
          ) : (
            <RetrievalForm archives={page.items.map((a) => ({ id: a.id, name: a.name }))} />
          )
        }
      </ApiState>
    </>
  );
}

function RetrievalForm({ archives }: { archives: { id: string; name: string }[] }) {
  const { t } = useTranslation();
  const lang = useLang();
  const toast = useToast();
  const [archiveId, setArchiveId] = useState(archives[0].id);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const objects = useAsync(() => api.listObjects(archiveId, 200), [archiveId]);

  const [estimate, setEstimate] = useState<RetrievalEstimate | null>(null);
  const [result, setResult] = useState<RetrievalResult | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [delivering, setDelivering] = useState(false);

  useEffect(() => {
    setSelected(new Set());
    setEstimate(null);
    setResult(null);
    setError(null);
    setPending(false);
  }, [archiveId]);

  const deliver = async () => {
    setDelivering(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.retrieve(archiveId, [...selected]);
      setResult(res);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, String(err)));
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setDelivering(false);
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const requestEstimate = async () => {
    setBusy(true);
    setError(null);
    setPending(false);
    setEstimate(null);
    try {
      const res = await api.estimateRetrieval(archiveId, [...selected]);
      setEstimate(res);
    } catch (err) {
      if (err instanceof ApiError && err.isNotImplemented) setPending(true);
      else setError(err instanceof ApiError ? err : new ApiError(0, String(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <Card>
        <div className="card__body">
          <div className="form-row" style={{ marginBottom: 16 }}>
            <Field label={t('retrieval.pickArchive')}>
              {(p) => (
                <select className="select" {...p} value={archiveId} onChange={(e) => setArchiveId(e.target.value)}>
                  {archives.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="field__label" style={{ marginBottom: 8 }}>
              {t('retrieval.pickObjects')}
            </legend>
            <ApiState {...objects} onRetry={objects.reload}>
              {(page) =>
                page.items.length === 0 ? (
                  <p className="muted">{t('retrieval.noObjects')}</p>
                ) : (
                  <div className="stack" style={{ gap: 0 }}>
                    {page.items.map((o) => (
                      <label key={o.id} className="upload-item" style={{ cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selected.has(o.id)}
                          onChange={() => toggle(o.id)}
                          style={{ width: 18, height: 18 }}
                        />
                        <span className="upload-item__info">
                          <span className="upload-item__name">{o.path}</span>
                          <span className="upload-item__meta">{formatBytes(o.bytes, lang)}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )
              }
            </ApiState>
          </fieldset>

          <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="secondary" onClick={requestEstimate} disabled={busy || delivering || selected.size === 0}>
              {busy ? <Spinner /> : <IconRetrieval width={18} height={18} />}
              {t('retrieval.estimate')}
            </Button>
            <Button variant="primary" onClick={deliver} disabled={busy || delivering || selected.size === 0} title={t('retrieval.deliverHint')}>
              {delivering ? <Spinner /> : <IconCloud width={18} height={18} />}
              {delivering ? t('retrieval.delivering') : t('retrieval.deliver')}
            </Button>
            {selected.size === 0 ? <span className="muted">{t('retrieval.selectHint')}</span> : null}
          </div>
        </div>
      </Card>

      {pending ? (
        <Card>
          <PendingState />
        </Card>
      ) : error ? (
        <Card>
          <ErrorState error={error} onRetry={requestEstimate} />
        </Card>
      ) : estimate ? (
        <Card>
          <CardHead title={t('retrieval.resultTitle')} />
          <div className="card__body">
            <div className="grid grid--stats">
              <Stat label={t('retrieval.media')} value={formatNumber(estimate.media, lang)} icon={<IconRetrieval width={16} height={16} />} />
              <Stat label={t('retrieval.days')} value={t('retrieval.daysValue', { count: estimate.days })} />
              <Stat label={t('retrieval.eur')} value={formatEUR(estimate.eur, lang)} icon={<IconBilling width={16} height={16} />} />
            </div>
            <div className={`alert ${estimate.spanned ? 'alert--warning' : 'alert--info'}`} style={{ marginTop: 16, marginBottom: 0 }}>
              {estimate.spanned ? <IconWarning width={18} height={18} /> : <IconCheck width={18} height={18} />}
              <span>{estimate.spanned ? t('retrieval.spanned') : t('retrieval.notSpanned')}</span>
            </div>
          </div>
        </Card>
      ) : null}

      {result ? (
        <Card>
          <CardHead title={t('retrieval.deliveredTitle')} />
          <div className="card__body">
            <div className="grid grid--stats" style={{ marginBottom: 16 }}>
              <Stat label={t('retrieval.media')} value={formatNumber(result.objectCount, lang)} icon={<IconCloud width={16} height={16} />} />
              <Stat label={t('common.size')} value={formatBytes(result.bytes, lang)} icon={<IconBilling width={16} height={16} />} />
            </div>
            <div className="stack" style={{ gap: 8, marginBottom: 16 }}>
              <div className="form-row" style={{ alignItems: 'center', gap: 8 }}>
                <span className="field__label">{t('retrieval.bucket')}:</span>
                <IdChip id={result.bucket} />
              </div>
              <div className="muted mono" style={{ fontSize: 13 }}>
                {t('retrieval.endpoint')}: {result.endpoint}
              </div>
              <div className="alert alert--info" style={{ margin: 0 }}>
                <IconCheck width={18} height={18} />
                <span>{t('retrieval.expires', { count: result.expiresInHours })}</span>
              </div>
            </div>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>{t('common.name')}</th>
                    <th className="num">{t('common.size')}</th>
                    <th className="num">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.objects.map((o) => (
                    <tr key={o.path}>
                      <td className="cell-strong mono">{o.path}</td>
                      <td className="num">{formatBytes(o.bytes, lang)}</td>
                      <td className="num">
                        <a className="btn btn--secondary btn--sm" href={o.url} target="_blank" rel="noopener noreferrer">
                          <IconDownload width={16} height={16} />
                          {t('retrieval.download')}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
